import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact name + phone lead-capture form for mid-content placement on
 * service pages, location pages and blog posts. Posts to /api/contact,
 * which emails the firm. `source` and `service` give context in the email.
 */
export const InlineLeadForm = ({
  source = "Website",
  service,
  heading = "Talk to a lawyer — free first consultation",
  subline = "Share your name and number. A partner will call you back within one business day.",
  testid = "inline-lead-form",
  variant = "light",
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const dark = variant === "dark";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Please enter your name.");
    if (!/^[0-9+\-\s()]{8,}$/.test(phone.trim())) return toast.error("Please enter a valid phone number.");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        phone: phone.trim(),
        service: service || undefined,
        subject: "Quick Call-Back Request",
        source,
        website,
      });
      setDone(true);
      toast.success("Request received. We'll call you back shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call +91 96435 72870.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testid}-success`}
        className={`my-10 md:my-12 p-6 md:p-10 border ${dark ? "bg-ink text-white border-white/20" : "bg-bone border-rule"} flex items-start gap-4`}
      >
        <CheckCircle2 className={`w-7 h-7 shrink-0 ${dark ? "text-white" : "text-brand"}`} strokeWidth={1.5} />
        <div>
          <h3 className={`font-display text-2xl ${dark ? "text-white" : "text-ink"}`}>Thank you, {name.split(" ")[0]}.</h3>
          <p className={`font-serif text-lg mt-2 ${dark ? "text-white/80" : "text-ink/70"}`}>
            Your request has reached our team. A K & K Associates partner will call you back within one business day.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      className={`my-10 md:my-12 p-6 md:p-10 border ${dark ? "bg-ink text-white border-white/20" : "bg-bone border-rule"}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <Phone className={`w-5 h-5 ${dark ? "text-white" : "text-brand"}`} strokeWidth={1.5} />
        <p className={`kicker ${dark ? "text-white/70" : ""}`}>Request a Call Back</p>
      </div>
      <h3 className={`font-display text-2xl md:text-3xl leading-tight ${dark ? "text-white" : "text-ink"}`}>{heading}</h3>
      <p className={`font-serif text-base md:text-lg mt-3 mb-6 ${dark ? "text-white/75" : "text-ink/70"}`}>{subline}</p>
      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="hidden"
          data-testid={`${testid}-website`}
        />
        <Input
          data-testid={`${testid}-name`}
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={dark ? "bg-white/10 border-white/25 text-white placeholder:text-white/50 h-12" : "bg-paper h-12"}
          aria-label="Your name"
        />
        <Input
          data-testid={`${testid}-phone`}
          type="tel"
          placeholder="Phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={dark ? "bg-white/10 border-white/25 text-white placeholder:text-white/50 h-12" : "bg-paper h-12"}
          aria-label="Phone number"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testid}-submit`}
          className={`h-12 px-7 inline-flex items-center justify-center gap-2 text-xs tracking-[0.2em] uppercase font-medium transition-colors disabled:opacity-60 ${dark ? "bg-white text-ink hover:bg-white/90" : "bg-brand text-white hover:bg-brand/90"}`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Request Call <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></>}
        </button>
      </form>
      <p className={`text-xs mt-4 ${dark ? "text-white/50" : "text-ink/45"}`}>
        Your details are confidential and protected by attorney–client privilege.
      </p>
    </div>
  );
};

export default InlineLeadForm;
